'use client';

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, CheckCircle2, Clock, Truck, MapPin, AlertCircle, ChevronRight, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { OrdersApi } from '@/lib/Apis/apis/orders-api';
import { PopulatedOrder } from '@/lib/Apis/models';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { formatPrice } from '@/lib/utils/format';

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  Pending: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: <Clock className="w-4 h-4" /> },
  'Picking & Packing': { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: <Package className="w-4 h-4" /> },
  'Ready for Pickup': { color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: <MapPin className="w-4 h-4" /> },
  'Picked Up': { color: 'text-green-700', bg: 'bg-green-50 border-green-200', icon: <CheckCircle2 className="w-4 h-4" /> },
  'Out for Delivery': { color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200', icon: <Truck className="w-4 h-4" /> },
  Delivered: { color: 'text-green-700', bg: 'bg-green-50 border-green-200', icon: <CheckCircle2 className="w-4 h-4" /> },
  'On Hold': { color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: <AlertCircle className="w-4 h-4" /> },
  Cancelled: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: <AlertCircle className="w-4 h-4" /> },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200', icon: <Package className="w-4 h-4" /> };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold ${cfg.bg} ${cfg.color}`}>
      {cfg.icon}
      {status}
    </span>
  );
}

// ─── Timeline item ─────────────────────────────────────────────────────────────

interface TimelineEntry {
  title: string;
  description?: string;
  date?: string;
  type?: string;
}

function TimelineItem({ entry, isLast }: { entry: TimelineEntry; isLast: boolean }) {
  const isDelivered = entry.type === 'delivered' || entry.title?.toLowerCase().includes('delivered');
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 ${isDelivered ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-500'}`}>
          <CheckCircle2 className="w-4 h-4" />
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1 mb-1 min-h-[24px]" />}
      </div>
      <div className="pb-5 flex-1">
        <p className="font-semibold text-gray-900 text-sm">{entry.title}</p>
        {entry.description && <p className="text-sm text-gray-500 mt-0.5">{entry.description}</p>}
        {entry.date && (
          <p className="text-xs text-gray-400 mt-1">{new Date(entry.date).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export interface TrackOrderScreenProps {
  /** Pre-fill the order ID field (e.g. from the confirmation page). */
  initialOrderId?: string;
  /** Pre-fill the email field. */
  initialEmail?: string;
}

export function TrackOrderScreen({ initialOrderId = '', initialEmail = '' }: TrackOrderScreenProps) {
  const [orderId, setOrderId] = useState(initialOrderId);
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PopulatedOrder | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleTrack = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedId = orderId.trim();
    const trimmedEmail = email.trim();
    if (!trimmedId || !trimmedEmail) {
      setError('Please enter both your Order ID and email address.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const api = new OrdersApi(AXIOS_CONFIG);
      const res = await api.trackOrder(trimmedId, trimmedEmail);
      setResult(res.data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      const msg = err?.response?.data?.message || '';
      if (err?.response?.status === 404 || msg.toLowerCase().includes('not found')) {
        setError('No order found for that combination. Please check your Order ID and email.');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const timeline: TimelineEntry[] = Array.isArray(result?.orderTimeLine)
    ? result!.orderTimeLine.map((entry: any) =>
        typeof entry === 'string' ? { title: entry } : entry
      )
    : [];

  const items: any[] = Array.isArray(result?.items)
    ? result!.items.map((item: any) =>
        typeof item === 'string' ? { name: item } : item
      )
    : [];

  return (
    <div className="min-h-screen bg-white pb-16">
      <div className="max-w-2xl mx-auto px-4 pt-12">
        {/* Header */}
        <div className="mb-10">
          <p className="text-xs font-semibold tracking-[0.2em] text-hprimary/50 uppercase mb-2">Order Tracking</p>
          <h1 className="text-3xl font-bold text-[#2B4B7C] mb-2">Track Your Order</h1>
          <p className="text-gray-500 text-sm">Enter your order ID and email to see real-time status updates.</p>
        </div>

        {/* Search form */}
        <form onSubmit={handleTrack} className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8 space-y-5 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Order ID <span className="text-red-400">*</span>
            </label>
            <Input
              placeholder="e.g. 6847a3f2c1..."
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              leftElement={<Package className="h-4 w-4 text-gray-400" />}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address <span className="text-red-400">*</span>
            </label>
            <Input
              type="email"
              placeholder="the email you used at checkout"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftElement={<Search className="h-4 w-4 text-gray-400" />}
            />
          </div>

          {error && (
            <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-hprimary text-white text-sm font-semibold transition-opacity disabled:opacity-60"
          >
            {isLoading ? 'Searching…' : <>Track Order <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              ref={resultRef}
              key="track-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="space-y-6"
            >
              {/* Status card */}
              <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Order ID</p>
                    <p className="font-mono text-sm text-gray-700 break-all">{(result as any).orderId || result._id || result.id}</p>
                  </div>
                  <StatusBadge status={result.orderStatus ?? 'Pending'} />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Type</p>
                    <p className="font-medium text-gray-700">{result.orderType}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Date</p>
                    <p className="font-medium text-gray-700">{result.createdAt ? new Date(result.createdAt).toLocaleDateString() : '—'}</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              {timeline.length > 0 && (
                <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                  <h2 className="font-semibold text-[#2B4B7C] text-lg mb-6">Order Timeline</h2>
                  <div>
                    {timeline.map((entry, i) => (
                      <TimelineItem key={i} entry={entry} isLast={i === timeline.length - 1} />
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              {items.length > 0 && (
                <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                  <h2 className="font-semibold text-[#2B4B7C] text-lg mb-5">Items Ordered</h2>
                  <div className="space-y-4">
                    {items.map((item, i) => (
                      <div key={i} className="flex items-center gap-4">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-14 h-14 rounded-xl object-cover bg-gray-100 shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                          {item.variantName && (
                            <p className="text-xs text-gray-500 mt-0.5">{item.variantName}</p>
                          )}
                          {item.quantity && (
                            <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                          )}
                        </div>
                        {item.price != null && (
                          <p className="text-sm font-semibold text-gray-900 shrink-0">{formatPrice(item.price)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price breakdown */}
              <div className="bg-white border-2 border-gray-100 rounded-[24px] p-6 sm:p-8">
                <h2 className="font-semibold text-[#2B4B7C] text-lg mb-5">Price Summary</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium text-gray-900">{formatPrice(result.subTotal ?? 0)}</span>
                  </div>
                  {(result.shippingCost ?? 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Shipping</span>
                      <span className="font-medium text-gray-900">{formatPrice(result.shippingCost ?? 0)}</span>
                    </div>
                  )}
                  {(result.tax ?? 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tax</span>
                      <span className="font-medium text-gray-900">{formatPrice(result.tax ?? 0)}</span>
                    </div>
                  )}
                  {(result.discountedAmount ?? 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Discount</span>
                      <span className="font-medium text-green-600">− {formatPrice(result.discountedAmount ?? 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-gray-100">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-[#2B4B7C] text-base">{formatPrice(result.grandTotal ?? 0)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
